import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable, runInAction } from 'mobx';

import type { AuthorRef } from './TranslationMemoryStore';
import type {
  EntryProvenance,
  TranslationMemoryEntry,
  TranslationMemoryEntryInput,
} from '@src/repositories/TranslationMemoryEntryRepository';
import type { Result } from '@src/services/common/Result';
import type { TranslationMemoryEntryService } from '@src/services/TranslationMemoryEntryService';

const logger = LogManager.getLogger('stores:TranslationMemoryEntry');

type EntryStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface TranslationMemoryEntriesState {
  status: EntryStatus;
  entries: TranslationMemoryEntry[];
  error: string | null;
  /**
   * The paged read hit its ceiling (PLAT-10871) — `entries` is the first N, not
   * the whole workspace. The view says so rather than presenting a short list as
   * complete.
   */
  truncated: boolean;
}

interface Slice extends TranslationMemoryEntriesState {
  inFlight: Promise<void> | null;
}

const emptySlice = (): Slice => ({
  status: 'idle',
  entries: [],
  error: null,
  truncated: false,
  inFlight: null,
});

/**
 * Workspace translation-memory entries, cached per showcase so the Settings tab
 * loads them once and reuses them across opens and collection switches (the
 * view filters this list by the selected collection client-side). Mirrors
 * `TranslationMemoryStore`: an observable showcase→slice map replaced wholesale,
 * load-once unless forced, and mutations that apply the service's confirmed
 * result in place (GraphQL lags CMA writes, so a refetch would re-introduce a
 * just-deleted / omit a just-created entry).
 */
export class TranslationMemoryEntryStore {
  private slices = observable.map<string, Slice>(undefined, { deep: false });

  constructor(private service: TranslationMemoryEntryService) {
    makeAutoObservable<this, 'service'>(this, { service: false });
  }

  getState(showcaseId: string): TranslationMemoryEntriesState {
    const slice = this.slices.get(showcaseId) ?? emptySlice();
    return {
      status: slice.status,
      entries: slice.entries,
      error: slice.error,
      truncated: slice.truncated,
    };
  }

  loadForShowcase(
    showcaseId: string,
    options: { force?: boolean } = {},
  ): Promise<void> {
    const existing = this.slices.get(showcaseId) ?? emptySlice();
    if (!options.force) {
      if (existing.status === 'ready') return Promise.resolve();
      if (existing.inFlight) return existing.inFlight;
    }

    const promise = this.service.fetchForShowcase(showcaseId).then(result => {
      runInAction(() => {
        const current = this.slices.get(showcaseId) ?? emptySlice();
        if (result.ok) {
          this.slices.set(showcaseId, {
            status: 'ready',
            entries: result.value.entries,
            error: null,
            truncated: result.value.truncated,
            inFlight: null,
          });
        } else {
          logger.warn(
            'Failed to load translation memory entries',
            result.error,
          );
          this.slices.set(showcaseId, {
            status: 'error',
            entries: current.entries,
            error: "Couldn't load translation memory entries.",
            truncated: current.truncated,
            inFlight: null,
          });
        }
      });
    });

    this.slices.set(showcaseId, {
      ...existing,
      status: 'loading',
      error: null,
      inFlight: promise,
    });

    return promise;
  }

  /**
   * `provenance` records how the entry was added (E12) — omitted ⇒ `manual`
   * (Settings CRUD); the Translation Review promote flow passes
   * `{ origin: 'promoted' }` (PLAT-10852).
   */
  async createEntry(
    showcaseId: string,
    input: TranslationMemoryEntryInput,
    author: AuthorRef | null,
    provenance?: EntryProvenance,
  ): Promise<Result<TranslationMemoryEntry>> {
    const result = await this.service.create(
      showcaseId,
      input,
      author?.id ?? null,
      provenance,
    );
    if (!result.ok) return result;
    const created: TranslationMemoryEntry = {
      ...result.value,
      createdByUser: author ? { id: author.id, name: author.name } : null,
      lastUpdatedByUser: author ? { id: author.id, name: author.name } : null,
    };
    runInAction(() => {
      const current = this.slices.get(showcaseId) ?? emptySlice();
      this.slices.set(showcaseId, {
        ...current,
        status: 'ready',
        entries: sortByTerm([...current.entries, created]),
        inFlight: null,
      });
    });
    return { ok: true, value: created };
  }

  async updateEntry(
    showcaseId: string,
    id: string,
    input: TranslationMemoryEntryInput,
    author: AuthorRef | null,
  ): Promise<Result<TranslationMemoryEntry>> {
    const result = await this.service.update(id, input, author?.id ?? null);
    if (!result.ok) return result;
    runInAction(() => {
      const current = this.slices.get(showcaseId) ?? emptySlice();
      this.slices.set(showcaseId, {
        ...current,
        status: 'ready',
        // Merge the edited fields into the existing entry, preserving origin,
        // createdAt, and createdByUser (an edit never relabels an imported /
        // promoted entry as manual). Stamp the last editor + update time so the
        // "last updated by" line reflects this edit without a reload.
        entries: sortByTerm(
          current.entries.map(e =>
            e.id === id
              ? {
                  ...e,
                  sourceTerm: input.sourceTerm,
                  translation: input.translation,
                  comment: input.comment ?? '',
                  collectionId: input.collectionId,
                  sourceLanguage: input.sourceLanguage,
                  translationLanguage: input.translationLanguage,
                  updatedAt: new Date().toISOString(),
                  lastUpdatedByUser: author
                    ? { id: author.id, name: author.name }
                    : e.lastUpdatedByUser,
                }
              : e,
          ),
        ),
        inFlight: null,
      });
    });
    return result;
  }

  async deleteEntry(showcaseId: string, id: string): Promise<Result<void>> {
    const result = await this.service.delete(id);
    if (!result.ok) return result;
    runInAction(() => {
      const current = this.slices.get(showcaseId) ?? emptySlice();
      this.slices.set(showcaseId, {
        ...current,
        status: 'ready',
        entries: current.entries.filter(e => e.id !== id),
        inFlight: null,
      });
    });
    return result;
  }

  /**
   * Apply the entries written by a document import (PLAT-10848 / DS9) to the
   * cache in place — append the created ones, replace the overwritten ones by id
   * — so the entry table reflects the import without a refetch (GraphQL lags CMA
   * writes). No-op on an empty showcase slice that was never loaded.
   */
  applyImported(
    showcaseId: string,
    imported: {
      created: TranslationMemoryEntry[];
      overwritten: TranslationMemoryEntry[];
    },
  ): void {
    if (!imported.created.length && !imported.overwritten.length) return;
    runInAction(() => {
      const current = this.slices.get(showcaseId) ?? emptySlice();
      const overwrittenById = new Map(imported.overwritten.map(e => [e.id, e]));
      const merged = current.entries.map(e => overwrittenById.get(e.id) ?? e);
      this.slices.set(showcaseId, {
        ...current,
        status: 'ready',
        entries: sortByTerm([...merged, ...imported.created]),
        inFlight: null,
      });
    });
  }

  invalidate(showcaseId?: string): void {
    if (showcaseId) this.slices.delete(showcaseId);
    else this.slices.clear();
  }
}

const sortByTerm = (
  entries: TranslationMemoryEntry[],
): TranslationMemoryEntry[] =>
  [...entries].sort((a, b) =>
    a.sourceTerm.localeCompare(b.sourceTerm, undefined, {
      sensitivity: 'base',
    }),
  );
